import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { defaultSYS2010_R01Dto, SYS2010_R01Dto } from './Dto_SYS2010_R01';
import { showWarning } from 'src/utils/toast/toastUtil';

export interface SYS2010_R01Filter {
	menuCd: string;
	menuNm: string;
	useYn: string;
	devSts: string;
}

/**
 * Zustand 스토어의 상태 및 액션 정의
 */
export interface Store_SYS2010_R01 {
    // === 검색 관련 ===
	SYS2010_R01Filter: SYS2010_R01Filter;
	SYS2010_R01PastFilter: SYS2010_R01Filter;
	setSYS2010_R01Filter: (filter: Partial<SYS2010_R01Filter>) => void;
	setSYS2010_R01PastFilter: (filter: Partial<SYS2010_R01Filter>) => void;

    // === 리스트 관련 === 
    SYS2010_R01List: SYS2010_R01Dto[];
    setSYS2010_R01List: (SYS2010_R01List: SYS2010_R01Dto[]) => void;
    insertSYS2010_R01List: (data: SYS2010_R01Dto[], index?: number) => void;
    removeSYS2010_R01List: (valueIndex: number) => void;
    updateSYS2010_R01List: (valueIndex: number, data: SYS2010_R01Dto) => void;

    // === 삭제 관련 ===
    deletedSYS2010_R01List: string[];
    addDeletedSYS2010_R01List: (idList: string[]) => void;
    removeDeletedSYS2010_R01List: (idList?: string[]) => void;

    // === 선택 관련 ===
    selectedSYS2010_R01List: string[];
    setSelectedSYS2010_R01List: (idList: string[]) => void;
    addSelectedSYS2010_R01List: (idList: string[]) => void;
    removeSelectedSYS2010_R01List: (idList?: string[]) => void;
	initSelectedSYS2010_R01List: () => void;
	allSelectedSYS2010_R01List: () => void;

	originalSYS2010_R01List: SYS2010_R01Dto[];
	setOriginalSYS2010_R01List: (list: SYS2010_R01Dto[]) => void;

    // === AG Grid 컬럼 상태 관리 ===
    columnState: ColumnState[] | null;	    setColumnState: (state: ColumnState[] | null) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;

	allCount: number;				setAllCount: (allCount: number) => void;
	

    // === 전체 초기화 ===
    initData: () => void;
}

export const defaultSYS2010_R01Filter : SYS2010_R01Filter = {
	menuNm: '',
	menuCd: '',
	useYn: '',
	devSts: '',
};

/**
 * Zustand 스토어 정의
 */
export const useSYS2010_R01Store = create<Store_SYS2010_R01>((set) => ({
    // === 검색 관련 ===
	SYS2010_R01Filter: defaultSYS2010_R01Filter,
	SYS2010_R01PastFilter: defaultSYS2010_R01Filter,
	setSYS2010_R01Filter: (filter) => set((state) => ({ SYS2010_R01Filter: { ...state.SYS2010_R01Filter, ...filter } })),
	setSYS2010_R01PastFilter: (filter) => set((state) => ({ SYS2010_R01PastFilter: { ...state.SYS2010_R01PastFilter, ...filter } })),

    // === 리스트 관련 ===
    SYS2010_R01List: [],
    setSYS2010_R01List: (SYS2010_R01List: SYS2010_R01Dto[]) => set((state) => {
		let newList: SYS2010_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		SYS2010_R01List.forEach((item) => {
			// console.log(index + '번째 행의 item.Index 정보: ' 
			// 	+ ' valueIndex : ' + item.valueIndex 
			// 	+ ' valueAndOrderIndex : ' + valueAndOrderIndex
			// 	+ ' orderIndex : ' + item.orderIndex 
			// 	+ ' orderIndexSeq : ' + item.orderIndexSeq);
			newList.push({ 
				...defaultSYS2010_R01Dto,
				...item,
				manualPage: true
					? item.manualPage
					: typeof (item as any)?.manual_page === 'number'
						? (item as any).manual_page
						: defaultSYS2010_R01Dto.manualPage,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: Math.max(item.orderIndexSeq, 0)
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			SYS2010_R01List: newList
		}
	}),
    insertSYS2010_R01List: (data: SYS2010_R01Dto[], index?: number) => set((state) => {
		let newSYS2010_R01List = [...state.SYS2010_R01List];

		let targetRowData = newSYS2010_R01List.find(item => item.valueIndex === index);
		let targetOrderIndex = targetRowData?.orderIndex || 0;
		// 데이터에 orderIndex가 있으면 그것을 사용하고, 없으면 1 (index 파라미터는 배열 위치로만 사용)		

		const newOrderIndexSeq = (targetRowData?.orderIndexSeq || 0) + 1;
		// console.log('targetOrderIndex : ', targetOrderIndex);
		// console.log('newOrderIndexSeq : ', newOrderIndexSeq);
		let maxValueIndex = Math.max(0, ...newSYS2010_R01List.map(item => item.valueIndex || 0)) + 1;

		// newSYS2010_R01List에서 targetOrderIndex인 행들을 빼내서 newOrderIndexSeq 기준으로 다시 정렬
		// newOrderIndexSeq 보다 orderIndexSeq가 작은 행들은 유지. 
		// newOrderIndexSeq 보다 orderIndexSeq가 큰 행들은 기존 orderIndexSeq 값에 1을 더해서 저장.
		const targetRows = newSYS2010_R01List.filter(item => item.orderIndex === targetOrderIndex);
		targetRows.forEach(item => {
			if(item.orderIndexSeq < newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq;
			} else if(item.orderIndexSeq >= newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq + 1;
			}
		});

		// targetRows를 newSYS2010_R01List에 대체
		newSYS2010_R01List = newSYS2010_R01List.filter(item => item.orderIndex !== targetOrderIndex);
		newSYS2010_R01List.push(...targetRows);
		
		//새 행을 생성해서 newOrderIndexSeq 값의 행을 저장
		const newData = data.map(item => ({ ...defaultSYS2010_R01Dto, ...item, 
			valueIndex: maxValueIndex++,
			orderIndex: targetOrderIndex,
			orderIndexSeq: newOrderIndexSeq
		}));
		if (index !== undefined) {
			newSYS2010_R01List.splice(index, 0, ...newData);
		} else {
			newSYS2010_R01List.push(...newData);
		}

		return {
			...state,
			SYS2010_R01List: newSYS2010_R01List
		}
    }),
    removeSYS2010_R01List: (valueIndex: number) => set((state) => {
		const newSYS2010_R01List = [...state.SYS2010_R01List].filter((item) => item.valueIndex !== valueIndex);
		return {
			...state,
			SYS2010_R01List: newSYS2010_R01List
		}
	}),
    updateSYS2010_R01List: (valueIndex: number, data: SYS2010_R01Dto) => set((state) => {
		const _SYS2010_R01List = state.SYS2010_R01List.map(item =>
			item.valueIndex === valueIndex ? data : item
		);
		if(data.menuSeq && data.menuSeq < 1) {
			showWarning('메뉴순서는 1보다 작을 수 없습니다.');
			data.menuSeq = 1;
		}
		return {
			...state,
			SYS2010_R01List: _SYS2010_R01List
		}
    }),

    // === 삭제 관련 ===
    deletedSYS2010_R01List: [],
    addDeletedSYS2010_R01List: (idList: string[]) => set((state) => {
		const _deletedSYS2010_R01List = [...state.deletedSYS2010_R01List];

		for(const valueIndex of idList) {
			const isInserted = state.SYS2010_R01List.some(item => item.valueIndex === Number(valueIndex) && item.inserted);
			const isModified = state.SYS2010_R01List.some(item => item.valueIndex === Number(valueIndex) && item.isModified);
			if(!isInserted && !isModified) {
				_deletedSYS2010_R01List.push(valueIndex);
			}
		}
		return {
			...state,
			deletedSYS2010_R01List: _deletedSYS2010_R01List
		}
    }),
    removeDeletedSYS2010_R01List: (idList?: string[]) => set((state) => ({
        ...state,
        deletedSYS2010_R01List: state.deletedSYS2010_R01List.filter(value => idList ? !idList.includes(value) : false),
    })),

    // === 선택 관련 ===
    selectedSYS2010_R01List: [],
    setSelectedSYS2010_R01List: (idList: string[]) => set({ selectedSYS2010_R01List: idList }),
    addSelectedSYS2010_R01List: (idList: string[]) => set((state) => {
		const _selectedSYS2010_R01List = [...state.selectedSYS2010_R01List];
		if(idList.length > 0) {
			_selectedSYS2010_R01List.push(...idList);
		}
		return {
			...state,
			selectedSYS2010_R01List: _selectedSYS2010_R01List
		}
    }),
    removeSelectedSYS2010_R01List: (idList?: string[]) => set((state) => ({
        ...state,
        selectedSYS2010_R01List: state.selectedSYS2010_R01List.filter(value => idList ? !idList.includes(value) : false),
    })),
	initSelectedSYS2010_R01List: () => set({ selectedSYS2010_R01List: [] }),
	allSelectedSYS2010_R01List: () => set((state) => {
		const _selectedSYS2010_R01List = [...state.SYS2010_R01List.map(item => item.valueIndex.toString())];
		return {
			...state,
			selectedSYS2010_R01List: _selectedSYS2010_R01List
		}
	}),

	originalSYS2010_R01List: [],
	setOriginalSYS2010_R01List: (list: SYS2010_R01Dto[]) => set((state) => {
		let newList: SYS2010_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		list.forEach((item) => {
			newList.push({ 
				...defaultSYS2010_R01Dto,
				...item,
				manualPage: typeof item.manualPage === 'number'
					? item.manualPage
					: typeof (item as any)?.manual_page === 'number'
						? (item as any).manual_page
						: defaultSYS2010_R01Dto.manualPage,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			originalSYS2010_R01List: newList
		}
	}),


    // === AG Grid 컬럼 상태 관리 ===
    columnState: null,			setColumnState: (state) => set({ columnState: state }),

	scrollTop: null,
	setScrollTop: (top: number | null) => set({ scrollTop: top }),

	allCount: 0,				setAllCount: (allCount: number) => set({ allCount }),
						
 
    // === 전체 초기화 ===
    initData: () => set(() => ({
		SYS2010_R01Filter: defaultSYS2010_R01Filter,
		SYS2010_R01PastFilter: defaultSYS2010_R01Filter,
		selectedSystem: '',
		SYS2010_R01List: [],
		deletedSYS2010_R01List: [],
		selectedSYS2010_R01List: [],
		columnState: null,
		originalSYS2010_R01List: [],
		allCount: 0,
		scrollTop: null,
    })),
}));

export default useSYS2010_R01Store;
